import { Box, Button, Link } from '@99designs/ui';
import { colors, typography } from '@99designs/ui/settings';
import { Source } from '../../common/constants';
import { linkTo } from '../../common/linkTo';
import { OAuthProvider } from '../../common/types';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import React from 'react';
import { useAuthConfigContext } from '../AuthConfigContext';
import { formatMessage } from '@99designs/frontend-components/i18n/index';
import { VistaprintLogomark } from '@99designs/icons';
import styled from 'styled-components';

export interface OAuthContinueButtonProps {
    target?: string | null;
    provider: OAuthProvider;
    source?: Source;
    action: 'login' | 'signup';
}

const providerIcon = (provider: OAuthProvider) => {
    switch (provider) {
        case 'Vistaprint':
            return VistaprintLogomark;
        default:
            return undefined;
    }
};

const PuntRoadSubtleText = styled.small`
    color: ${colors.puntRoad};
    font-size: ${typography.typeScale.size2}px;
`;

const OAuthButtonMessage = {
    login: __('Log in with {provider}'),
    signup: __('Sign up with {provider}'),
    continue: __('Continue with {provider}'),
};

export function OAuthContinueButton({
    target = null,
    provider,
    source,
    action,
}: OAuthContinueButtonProps) {
    const { locale } = useAuthConfigContext();
    function onClick() {
        window.location.assign(linkTo.authOAuthRedirect(provider, target, source));
        return null;
    }

    return <OAuthButton provider={provider} action={action} locale={locale} onClick={onClick} />;
}

export interface OAuthButtonProps {
    provider: OAuthProvider;
    action: 'login' | 'signup' | 'continue';
    locale: string;
    onClick: () => void;
    loading?: boolean;
}

export function OAuthButton({
    provider,
    action,
    locale,
    onClick,
    loading = false,
}: OAuthButtonProps) {
    return (
        <>
            <Button
                data-testid="oauth-button"
                size="large"
                appearance="secondary"
                width="fill"
                icon={providerIcon(provider)}
                onClick={onClick}
                type="button"
                loading={loading}
            >
                {formatMessage(OAuthButtonMessage[action], {
                    provider: provider,
                })}
            </Button>
            {provider === 'Vistaprint' && (
                <Box paddingTop="space8">
                    <PuntRoadSubtleText>
                        <FormattedReactMessage
                            message={__(
                                'We will automatically add your name and email to your 99designs account. <Link>Privacy Policy</Link>'
                            )}
                            replacements={{
                                Link: (
                                    <Link
                                        href={
                                            locale === 'en-US'
                                                ? linkTo.vistaprintPrivacyPolicyUS()
                                                : linkTo.vistaprintPrivacyPolicy(locale)
                                        }
                                        size="size2"
                                    />
                                ),
                            }}
                        />
                    </PuntRoadSubtleText>
                </Box>
            )}
        </>
    );
}
